import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const SoftwareFAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleQuestion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqData = [
    {
      question: "What exactly is included in the $199 consultation?",
      answer: "The consultation includes a full 60-minute call with one of our senior AI engineers who will assess your current systems, identify AI integration opportunities, and develop a strategic roadmap for implementation. After the call, you'll receive a detailed strategy document with actionable recommendations and ROI projections. We also provide 7 days of follow-up email support to answer any additional questions."
    },
    {
      question: "How long does a typical AI development project take?",
      answer: "Our development timelines are significantly faster than industry standards due to our experienced senior engineering team. Simple AI integrations can be completed in 2-4 weeks, while more complex custom applications typically take 6-12 weeks. Enterprise-level solutions may require 3-6 months, but we pride ourselves on delivering working modules throughout the development process so you can start seeing benefits sooner."
    },
    {
      question: "What types of AI models do you work with?",
      answer: "We have extensive experience with all leading AI technologies and models, including GPT-4, Claude, Gemini, DALL-E, Midjourney, Whisper, and other specialized models. We also offer custom model development and fine-tuning services for businesses with specific requirements. Our engineers stay current with all the latest AI advancements to ensure you receive cutting-edge solutions."
    },
    {
      question: "What's your minimum project budget?",
      answer: "While our custom AI integration services begin at $3,500, we recommend a minimum budget of $1,000 for our smallest projects. For organizations looking to get started with AI without significant investment, our $199 consultation option provides substantial value and a clear roadmap you can implement independently or with our assistance."
    },
    {
      question: "How does your 100% money-back guarantee work?",
      answer: "Our guarantee is simple: if we fail to deliver the agreed-upon solution that meets the specifications in our contract, you'll receive a complete refund of your investment. We offer this guarantee because we're confident in our ability to deliver exceptional results, and we believe you shouldn't pay unless we meet your expectations. Our current client satisfaction rate is 98.7%."
    },
    {
      question: "Do I own the code and intellectual property?",
      answer: "Absolutely. All custom development work we complete for you becomes your intellectual property. We deliver the full source code and comprehensive documentation for your solution. You have complete ownership and can modify, extend, or maintain the system internally or with other vendors in the future if you choose to do so."
    }
  ];

  return (
    <FAQContainer id="faq">
      <SectionHeader>
        <FAQBadge>FAQ</FAQBadge>
        <SectionTitle>Answers To Your <HighlightText>AI DEVELOPMENT QUESTIONS</HighlightText></SectionTitle>
        <SectionDescription>
          Everything you need to know before starting your <strong>AI transformation journey</strong> with our team of experts.
        </SectionDescription>
      </SectionHeader>
      
      <AccordionContainer>
        {faqData.map((item, index) => (
          <FAQItem key={index} isActive={activeIndex === index}>
            <QuestionRow onClick={() => toggleQuestion(index)}>
              <Question>{item.question}</Question>
              <ToggleIcon isActive={activeIndex === index}>+</ToggleIcon>
            </QuestionRow>
            <AnswerContainer isActive={activeIndex === index}>
              <Answer>{item.answer}</Answer>
            </AnswerContainer>
          </FAQItem>
        ))}
      </AccordionContainer>
      
      <MoreQuestionsBox>
        <MoreQuestionsText>Have additional questions?</MoreQuestionsText>
        <ContactButton href="#contact">Schedule Your Consultation</ContactButton>
      </MoreQuestionsBox>
    </FAQContainer>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const FAQContainer = styled.section`
  padding: 100px 80px;
  background-color: #f8f9fa;
  
  @media (max-width: 991px) {
    padding: 80px 40px;
  }
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const FAQBadge = styled.div`
  display: inline-block;
  background-color: #e9f7fe;
  color: #024A5F;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 0.9rem;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HighlightText = styled.span`
  color: #FF4757;
  display: block;
  margin-top: 10px;
`;

const SectionDescription = styled.p`
  font-size: 1.2rem;
  color: #666;
  line-height: 1.6;
  
  strong {
    color: #024A5F;
  }
`;

const AccordionContainer = styled.div`
  max-width: 900px;
  margin: 0 auto 60px;
  animation: ${fadeIn} 1s ease-out;
`;

const FAQItem = styled.div`
  background: white;
  border-radius: ${({ isActive }) => isActive ? '12px' : '12px'};
  margin-bottom: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid ${({ isActive }) => isActive ? '#024A5F' : '#eaeaea'};
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  }
`;

const QuestionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f9f9f9;
  }
  
  @media (max-width: 768px) {
    padding: 18px 20px;
  }
`;

const Question = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ToggleIcon = styled.span`
  color: #024A5F;
  font-size: 1.5rem;
  font-weight: 700;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transform: ${({ isActive }) => isActive ? 'rotate(45deg)' : 'rotate(0)'};
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const AnswerContainer = styled.div`
  height: ${({ isActive }) => isActive ? 'auto' : '0'};
  max-height: ${({ isActive }) => isActive ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  background: rgba(2, 74, 95, 0.02);
  border-top: ${({ isActive }) => isActive ? '1px solid #eaeaea' : 'none'};
`;

const Answer = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: #666;
  padding: 20px 30px;
  margin: 0;
  
  @media (max-width: 768px) {
    padding: 15px 20px;
    font-size: 0.95rem;
  }
`;

const MoreQuestionsBox = styled.div`
  text-align: center;
  background: white;
  max-width: 700px;
  margin: 0 auto;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid #eaeaea;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const MoreQuestionsText = styled.p`
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ContactButton = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #024A5F, #0871A4);
  color: white;
  font-weight: 600;
  font-size: 1rem;
  padding: 14px 30px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(2, 74, 95, 0.2);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 25px rgba(2, 74, 95, 0.3);
  }
`;

export default SoftwareFAQ; 